import React, { useState } from 'react';
import { Task, TaskStatus } from 'public-contracts';
import { ProjectTaskCard } from './ProjectTaskCard';
import { useTranslation } from '../../hooks/useTranslation';
import { MoreVertical, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { User } from '../../services/usersService';

interface KanbanColumnProps {
  status: TaskStatus;
  tasks: Task[];
  users: User[];
  onTaskDrop: (taskId: string, newStatus: TaskStatus) => void;
  onAddTask?: (status: TaskStatus) => void;
  onEditTask?: (task: Task) => void;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  tasks,
  users,
  onTaskDrop,
  onAddTask,
  onEditTask,
}) => {
  const { translate } = useTranslation();
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(false);
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId) {
      onTaskDrop(taskId, status);
    }
  };

  const onDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  const getStatusLabel = (status: TaskStatus) => {
    return translate(`pos.project_management.status.${status}`, status);
  };

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.TODO:
        return 'bg-slate-100 border-slate-200';
      case TaskStatus.IN_PROGRESS:
        return 'bg-blue-50 border-blue-100';
      case TaskStatus.REVIEW:
        return 'bg-purple-50 border-purple-100';
      case TaskStatus.DONE:
        return 'bg-green-50 border-green-100';
      case TaskStatus.BLOCKED:
        return 'bg-red-50 border-red-100';
      default:
        return 'bg-gray-50 border-gray-100';
    }
  };

  return (
    <div
      className={`flex flex-col w-80 min-w-[320px] rounded-lg border h-full transition-colors ${
        getStatusColor(status)
      } ${isOver ? 'ring-2 ring-primary ring-inset' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
            {getStatusLabel(status)}
          </h3>
          <span className="bg-background px-2 py-0.5 rounded-full text-xs font-medium border">
            {tasks.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onAddTask?.(status)}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tasks Container */}
      <div className="flex-1 overflow-y-auto p-2 min-h-[150px]">
        {tasks.map((task) => (
          <ProjectTaskCard
            key={task.id}
            task={task}
            users={users}
            onEdit={onEditTask}
            onDragStart={onDragStart}
          />
        ))}
        {tasks.length === 0 && !isOver && (
          <div className="h-24 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground text-xs italic">
            {translate('pos.project_management.no_tasks', 'No tasks')}
          </div>
        )}
      </div>
    </div>
  );
};
